import React, { useState, useRef, useCallback } from 'react';
import { clsx } from 'clsx';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { PlanRectangle } from '../../services/planService';

interface MindMapNodeProps {
  rectangle: PlanRectangle;
  x: number;
  y: number;
  width: number;
  height: number;
  isSelected: boolean;
  hasChildren: boolean;
  onSelect: () => void;
  onDrag: (id: number, x: number, y: number) => void;
  onDragEnd: (id: number, x: number, y: number) => void;
  onAddChild: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onView: () => void;
  scale: number;
}

const MindMapNode: React.FC<MindMapNodeProps> = ({
  rectangle,
  x,
  y,
  width,
  height,
  isSelected,
  hasChildren,
  onSelect,
  onDrag,
  onDragEnd,
  onAddChild,
  onEdit,
  onDelete,
  onView,
  scale,
}) => {
  const nodeRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x, y });

  // Update position when props change
  React.useEffect(() => {
    if (!isDragging) {
      setPosition({ x, y });
    }
  }, [x, y, isDragging]);

  const hasLinkedUsers = rectangle.linkedUserIds && rectangle.linkedUserIds.length > 0;
  const hasLinkedTasks = rectangle.linkedTaskIds && rectangle.linkedTaskIds.length > 0;

  // Get contrasting text color
  const getContrastColor = (hexColor: string) => {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#1f2937' : '#ffffff';
  };

  const textColor = getContrastColor(rectangle.color);

  // Handle drag start
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0 || e.altKey) return;
    e.stopPropagation();
    
    const rect = nodeRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    setIsDragging(true);
    setDragOffset({
      x: (e.clientX - rect.left) / scale,
      y: (e.clientY - rect.top) / scale,
    });
    
    onSelect();
  };

  // Handle drag move
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !nodeRef.current) return;
    
    const parent = nodeRef.current.parentElement;
    if (!parent) return;
    
    const parentRect = parent.getBoundingClientRect();
    const newX = (e.clientX - parentRect.left) / scale - dragOffset.x;
    const newY = (e.clientY - parentRect.top) / scale - dragOffset.y;
    
    setPosition({ x: newX, y: newY });
    onDrag(rectangle.id, newX, newY);
  }, [isDragging, dragOffset, scale, rectangle.id, onDrag]);

  // Handle drag end
  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      onDragEnd(rectangle.id, position.x, position.y);
    }
  }, [isDragging, rectangle.id, position, onDragEnd]);

  // Add/remove event listeners
  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div
      ref={nodeRef}
      className={clsx(
        'absolute group select-none',
        isDragging ? 'cursor-grabbing z-50' : 'cursor-grab',
        isSelected && 'z-40'
      )}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${width}px`,
        minHeight: `${height}px`,
      }}
      onMouseDown={handleMouseDown}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
    >
      {/* Node Content */}
      <div
        className={clsx(
          'relative w-full h-full rounded-xl shadow-lg transition-all duration-200',
          'border-2',
          isSelected
            ? 'ring-4 ring-primary-400 ring-offset-2 shadow-xl'
            : 'hover:shadow-xl hover:scale-[1.02]',
          isDragging && 'shadow-2xl scale-105'
        )}
        style={{
          backgroundColor: rectangle.color,
          borderColor: `${rectangle.color}88`,
        }}
      >
        {/* Content area */}
        <div className="p-4">
          <h4
            className="font-bold text-base leading-tight"
            style={{ color: textColor }}
          >
            {rectangle.title}
          </h4>

          {rectangle.content && (
            <p
              className="text-sm mt-2 leading-snug opacity-90 line-clamp-3"
              style={{ color: textColor }}
            >
              {rectangle.content}
            </p>
          )}

          {/* Links indicators */}
          {(hasLinkedUsers || hasLinkedTasks) && (
            <div className="flex items-center gap-2 mt-3">
              {hasLinkedUsers && (
                <span
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-white/30"
                  style={{ color: textColor }}
                >
                  <UserGroupIcon className="h-3.5 w-3.5" />
                  {rectangle.linkedUserIds.length}
                </span>
              )}
              {hasLinkedTasks && (
                <span
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-white/30"
                  style={{ color: textColor }}
                >
                  <ClipboardDocumentListIcon className="h-3.5 w-3.5" />
                  {rectangle.linkedTaskIds.length}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons - visible on hover */}
        <div className={clsx(
          'absolute -top-3 -right-3 flex items-center gap-1',
          'opacity-0 group-hover:opacity-100 transition-opacity duration-200',
          isSelected && 'opacity-100'
        )}>
          <button
            onClick={(e) => { e.stopPropagation(); onView(); }}
            className="p-2 rounded-full bg-white shadow-lg hover:bg-purple-50 hover:scale-110 transition-all"
            title="View details"
          >
            <EyeIcon className="h-4 w-4 text-purple-600" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onAddChild(); }}
            className="p-2 rounded-full bg-white shadow-lg hover:bg-primary-50 hover:scale-110 transition-all"
            title="Add child node"
          >
            <PlusIcon className="h-4 w-4 text-primary-600" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="p-2 rounded-full bg-white shadow-lg hover:bg-blue-50 hover:scale-110 transition-all"
            title="Edit node"
          >
            <PencilIcon className="h-4 w-4 text-blue-600" />
          </button>
          {!hasChildren && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="p-2 rounded-full bg-white shadow-lg hover:bg-red-50 hover:scale-110 transition-all"
              title="Delete node"
            >
              <TrashIcon className="h-4 w-4 text-red-600" />
            </button>
          )}
        </div>

        {/* Connection point on the right */}
        <div
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-3 h-3 rounded-full bg-white border-2 border-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Connect to child"
        />

        {/* Connection point on the left */}
        {rectangle.parentId && (
          <div
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-white border-2 border-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
          />
        )}
      </div>
    </div>
  );
};

export default MindMapNode;

